import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Wand2, Rocket, Loader2, CheckCircle2, XCircle, CircleDashed, Circle, Sparkles, StopCircle, PartyPopper, History, RotateCw, Link as LinkIcon } from "lucide-react";
import confetti from "canvas-confetti";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const PHASE_ICON = (status) => {
  if (status === "running") return <Loader2 className="w-4 h-4 animate-spin text-[#38bdf8]" />;
  if (status === "done") return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
  if (status === "skipped") return <CircleDashed className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />;
  if (status === "failed") return <XCircle className="w-4 h-4 text-rose-500" />;
  return <Circle className="w-4 h-4 text-[hsl(var(--muted-foreground))] opacity-40" />;
};
const RSTATUS = {
  ready: { label: "Ready to ship", emoji: "🚀", color: "#10b981" },
  almost: { label: "Almost there", emoji: "⏳", color: "#f59e0b" },
  "not-yet": { label: "In development", emoji: "🔧", color: "#f43f5e" },
};
const relColor = (s) => (RSTATUS[s] || RSTATUS["not-yet"]).color;

// Autonomous Game Director — one hands-off run that chains the whole Compiler pipeline (blueprint →
// build + auto-fix → Visual QA → playtest → release gate) with a live mission-control dashboard.
// Opt-in Auto-Ship publishes to the Arcade the instant it passes. History lets creators re-run any game.
export default function AutoDirector({ gameId, capture, title, onConfig, refresh }) {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPrev, setLoadingPrev] = useState(false);
  const [run, setRun] = useState(null);
  const [runId, setRunId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [autoShip, setAutoShip] = useState(false);
  const [history, setHistory] = useState([]);
  const [copiedShip, setCopiedShip] = useState(false);
  const pollRef = useRef(null);
  const shotRef = useRef(false);
  const celebratedRef = useRef(false);

  const running = run?.status === "running";

  const loadPreview = useCallback(async () => {
    setLoadingPrev(true);
    try { const { data } = await api.post(`/compiler/${gameId}/auto-direct/preview`); setPreview(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't prepare the run."); }
    setLoadingPrev(false);
  }, [gameId]);

  const loadHistory = useCallback(async () => {
    try { const { data } = await api.get(`/compiler/director-history`); setHistory(data.runs || []); } catch { /* noop */ }
  }, []);

  // Auto-open when arriving via a "re-run" deep link (?autodirect=1).
  useEffect(() => {
    if (sp.get("autodirect") === "1") {
      setOpen(true);
      const next = new URLSearchParams(sp); next.delete("autodirect"); setSp(next, { replace: true });
    }
  }, [sp, setSp]);

  useEffect(() => {
    if (!open) return;
    let live = true;
    loadHistory();
    api.get(`/compiler/${gameId}/auto-direct/latest`).then(({ data }) => {
      if (!live) return;
      const r = data.run;
      if (r && r.status === "running") { setRun(r); setRunId(r.id); }
      else loadPreview();
    }).catch(() => loadPreview());
    return () => { live = false; };
  }, [open, gameId, loadPreview, loadHistory]);

  const stopPoll = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };

  const tick = useCallback(async (id) => {
    try {
      const { data } = await api.get(`/compiler/${gameId}/auto-direct/${id}`);
      const r = data.run;
      setRun(r);
      if (r.status === "running" && r.awaiting === "screenshot" && !shotRef.current) {
        shotRef.current = true;
        try {
          const img = capture ? await capture() : null;
          if (img) await api.post(`/compiler/${gameId}/auto-direct/${id}/screenshot`, { image: img }, { timeout: 90000 });
        } catch { /* best-effort — backend skips visual if no shot */ }
      }
      if (r.awaiting !== "screenshot") shotRef.current = false;

      if (["done", "stopped", "failed"].includes(r.status)) {
        stopPoll();
        if (r.config && onConfig) onConfig(r.config);
        if (refresh) refresh();
        loadHistory();
        if (r.status === "done" && r.result?.status === "ready" && !celebratedRef.current) {
          celebratedRef.current = true;
          confetti({ particleCount: 180, spread: 100, origin: { y: 0.4 }, colors: ["#10b981", "#38bdf8", "#a855f7", "#fbbf24"] });
        }
        return;
      }
      pollRef.current = setTimeout(() => tick(id), 2000);
    } catch {
      pollRef.current = setTimeout(() => tick(id), 3000);
    }
  }, [gameId, capture, onConfig, refresh, loadHistory]);

  useEffect(() => {
    if (!runId || !open) return;
    shotRef.current = false; celebratedRef.current = false;
    tick(runId);
    return stopPoll;
  }, [runId, open, tick]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post(`/compiler/${gameId}/auto-direct`, { confirm: true, target: 80, auto_ship: autoShip }, { timeout: 60000 });
      setRun({ status: "running", phases: (preview?.plan || []).map((p) => ({ ...p, status: "pending" })), current: "blueprint", logs: [] });
      setRunId(data.run_id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start — try again.");
    }
    setStarting(false);
  };

  const stop = async () => {
    try { await api.post(`/compiler/${gameId}/auto-direct/${runId}/stop`); } catch { /* noop */ }
    toast.message("Stopping after the current step — your progress is saved.");
  };

  const reset = () => { stopPoll(); setRun(null); setRunId(null); setPreview(null); celebratedRef.current = false; loadPreview(); };
  const rerun = (r) => { if (r.game_id === gameId) reset(); else navigate(`/app/compiler/${r.game_id}?autodirect=1`); };

  const copyShip = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/api/og/release/${gameId}`); setCopiedShip(true); setTimeout(() => setCopiedShip(false), 1800); } catch { /* noop */ }
  };

  const res = run?.result;
  const rs = res ? (RSTATUS[res.status] || RSTATUS["not-yet"]) : null;
  const logs = (run?.logs || []).slice(-6);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) stopPoll(); }}>
      <DialogTrigger asChild>
        <Button className="rounded-full gap-1.5 h-7 px-3 text-xs bg-gradient-to-r from-[#7c3aed] via-[#6366f1] to-[#0ea5e9] text-white font-bold border-0" data-testid="auto-director-btn">
          <Wand2 className="w-3.5 h-3.5" /> Auto-Direct
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="auto-director-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5 text-[#7c3aed]" /> Autonomous Game Director</DialogTitle>
        </DialogHeader>

        {!run && (
          <div className="space-y-3" data-testid="auto-director-preview">
            <p className="text-sm text-[hsl(var(--muted-foreground))] leading-snug">
              One hands-off run takes <b>{title || "your game"}</b> all the way to <b>ship-ready</b>: it blueprints the vision, builds every missing system, auto-repairs weak spots, has the Art Director polish the look, playtests it, and runs the release gate — while you watch.
            </p>
            {loadingPrev ? <div className="py-6 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--muted-foreground))]" /></div> : preview && (
              <>
                <div className="rounded-xl border border-[hsl(var(--border))] p-3 space-y-1.5">
                  {preview.plan.map((p, i) => (
                    <div key={p.key} className="flex items-center gap-2 text-[13px]" data-testid={`auto-director-plan-${p.key}`}>
                      <span className="w-5 h-5 rounded-full bg-[hsl(var(--secondary))] grid place-items-center text-[10px] font-bold">{i + 1}</span>
                      {p.label}
                    </div>
                  ))}
                </div>
                <label className="flex items-center gap-2.5 rounded-xl border border-[hsl(var(--border))] px-3 py-2.5 cursor-pointer hover:border-[#7c3aed]/40 transition-colors" data-testid="auto-director-autoship-row">
                  <input type="checkbox" checked={autoShip} onChange={(e) => setAutoShip(e.target.checked)} className="w-4 h-4 accent-[#7c3aed]" data-testid="auto-director-autoship" />
                  <div className="flex-1">
                    <div className="text-[13px] font-semibold flex items-center gap-1.5"><Rocket className="w-3.5 h-3.5 text-[#10b981]" /> Auto-publish to the Arcade when it passes</div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))]">Goes live + copies the release link the instant it hits "Ready".</div>
                  </div>
                </label>
                <div className="flex items-center justify-between text-[12px] text-[hsl(var(--muted-foreground))]">
                  <span>Now: <b>{preview.depth_now}%</b> · target <b>{preview.depth_target}%</b></span>
                  <span>~<b>{preview.estimate.credits} credits</b> · {preview.credits_remaining} left</span>
                </div>
                {!preview.blueprint_ready && <p className="text-[11px] text-[hsl(var(--muted-foreground))]">Starts by drafting a full Game Blueprint (none yet).</p>}
                <Button onClick={start} disabled={starting || preview.credits_remaining < preview.estimate.credits} className="rounded-full w-full gap-2 font-bold bg-gradient-to-r from-[#7c3aed] to-[#0ea5e9] text-white" data-testid="auto-director-start">
                  {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Start autonomous build
                </Button>
                {preview.credits_remaining < preview.estimate.credits && <p className="text-[11px] text-rose-500 text-center">Not enough credits for a full run.</p>}

                {history.length > 0 && (
                  <div className="pt-1" data-testid="auto-director-history">
                    <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5"><History className="w-3.5 h-3.5" /> Recent autonomous runs</div>
                    <div className="space-y-1">
                      {history.slice(0, 6).map((r) => (
                        <div key={r.id} className="flex items-center gap-2 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1.5" data-testid={`auto-director-hist-${r.id}`}>
                          <span className="text-base shrink-0">{r.cover_emoji}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-[12px] font-medium truncate">{r.title}{r.game_id === gameId ? " (this game)" : ""}</div>
                            <div className="text-[10px] text-[hsl(var(--muted-foreground))]">
                              {r.status === "done" && r.result ? <span style={{ color: relColor(r.result.status) }}>{r.result.readiness}% · {(RSTATUS[r.result.status] || RSTATUS["not-yet"]).label}</span> : r.status}
                              {r.shipped ? " · 🚀 shipped" : ""}
                            </div>
                          </div>
                          <button onClick={() => rerun(r)} className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-bold border border-[hsl(var(--border))] hover:border-[#7c3aed]/50 shrink-0" data-testid={`auto-director-rerun-${r.id}`}>
                            <RotateCw className="w-3 h-3" /> Re-run
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {run && (
          <div className="space-y-3" data-testid="auto-director-run">
            <div className="space-y-1.5">
              {(run.phases || []).map((p) => (
                <div key={p.key} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 ${run.current === p.key && running ? "border-[#38bdf8]/50 bg-[#38bdf8]/5" : "border-[hsl(var(--border))]"}`} data-testid={`auto-director-phase-${p.key}`}>
                  {PHASE_ICON(p.status)}
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-medium">{p.label}</div>
                    {p.detail && <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{p.detail}</div>}
                  </div>
                </div>
              ))}
            </div>

            {logs.length > 0 && running && (
              <div className="rounded-lg bg-[hsl(var(--secondary))] px-3 py-2 space-y-0.5 max-h-24 overflow-y-auto" data-testid="auto-director-log">
                {logs.map((l, i) => <div key={i} className="text-[11px] text-[hsl(var(--muted-foreground))] leading-snug">{l.msg}</div>)}
              </div>
            )}

            {running && (
              <Button variant="outline" onClick={stop} className="rounded-full w-full gap-1.5 text-xs" data-testid="auto-director-stop"><StopCircle className="w-4 h-4" /> Stop after this step</Button>
            )}

            {run.status === "done" && res && (
              <div className="rounded-xl border p-4 text-center" style={{ borderColor: `${rs.color}55`, background: `${rs.color}0d` }} data-testid="auto-director-result">
                <div className="text-4xl font-extrabold tabular-nums" style={{ color: rs.color }}>{res.readiness ?? "—"}%</div>
                <div className="text-sm font-bold mt-0.5" style={{ color: rs.color }}>{rs.emoji} {rs.label}</div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1">Depth {res.depth ?? "—"}% · Visual {res.visual ?? "—"} · Playtest {res.playtest ?? "—"}</div>
                {run.shipped ? (
                  <div className="mt-2 text-[12px] font-semibold text-emerald-600 inline-flex items-center gap-1" data-testid="auto-director-shipped"><PartyPopper className="w-4 h-4" /> Live in the Arcade!</div>
                ) : res.status === "ready" && (
                  <div className="mt-2 text-[12px] font-semibold text-emerald-600 inline-flex items-center gap-1"><PartyPopper className="w-4 h-4" /> Passed the release gate!</div>
                )}
                <div className="flex gap-2 mt-3">
                  {run.shipped ? (
                    <button onClick={copyShip} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/40" data-testid="auto-director-copy-link"><LinkIcon className="w-3.5 h-3.5" /> {copiedShip ? "Copied!" : "Copy release link"}</button>
                  ) : (
                    <a href={`/release/${gameId}`} target="_blank" rel="noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/40"><Rocket className="w-3.5 h-3.5" /> Release page</a>
                  )}
                  <button onClick={reset} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-bold bg-gradient-to-r from-[#7c3aed] to-[#0ea5e9] text-white" data-testid="auto-director-again">Run again</button>
                </div>
              </div>
            )}
            {(run.status === "stopped" || run.status === "failed") && (
              <div className="text-center space-y-2" data-testid="auto-director-ended">
                <p className="text-[13px] text-[hsl(var(--muted-foreground))]">{run.status === "failed" ? "The run hit a snag — your saved progress is safe." : "Run stopped — your progress is saved."}</p>
                <button onClick={reset} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold bg-gradient-to-r from-[#7c3aed] to-[#0ea5e9] text-white" data-testid="auto-director-again">Start again</button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
